import React, { useState, useEffect, useCallback } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { ArrowLeft, RefreshCw, UploadCloud } from "lucide-react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import ConsumerTimeline from "../../components/ConsumerTimeline";
import { auth, db } from "../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import InfoModal from "../../components/InfoModal";
import CaseLimitDialog from "../../components/CaseLimitDialog";
import { getCaseStatus, getCaseHistory, orderPdfProxyUrl } from "../../services/ejagritiApi";
import { exportConsumerCase, checkConsumerCaseLimit, findDuplicateConsumerCase } from "../../services/ejagritiExport";

const openPdf = (url) => {
  const proxied = orderPdfProxyUrl(url);
  if (!proxied) return toast.error("No document available.");
  window.open(proxied, "_blank", "noopener,noreferrer");
};

function Detail({ label, value }) {
  const v = Array.isArray(value) ? value.filter(Boolean).join(", ") : value;
  return (
    <div>
      <div className="text-slate-500 text-xs mb-0.5">{label}</div>
      <div className="text-slate-700 font-semibold text-sm break-words">{v || "N/A"}</div>
    </div>
  );
}

function ViewBtn({ doc }) {
  if (!doc || !doc.available || !doc.pdfUrl) return <span className="text-white/30 text-xs">—</span>;
  return (
    <button
      onClick={() => openPdf(doc.pdfUrl)}
      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
    >
      View
    </button>
  );
}

export default function EJagritiCaseDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const caseNumber = location.state?.caseNumber;
  const [tab, setTab] = useState(location.state?.initialTab === "history" ? "history" : "status");

  const [statusData, setStatusData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [showAllOrders, setShowAllOrders] = useState(false); // History & Orders table: show 5, then View All
  const [order, setOrder] = useState("DESC");

  // ---- Export-to-Firebase state (mirrors High Court) ----
  const { globalUser } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupInfo, setDupInfo] = useState(null);
  const [showCaseLimitDialog, setShowCaseLimitDialog] = useState(false);
  const [caseLimitData, setCaseLimitData] = useState({ currentCount: 0, maxLimit: 0, planName: "" });
  const [userMobile, setUserMobile] = useState("");

  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    getDocs(query(collection(db, "lawyers"), where("uid", "==", u.uid)))
      .then((snap) => { if (!snap.empty) setUserMobile(snap.docs[0].data().mobile || ""); })
      .catch(() => {});
  }, []);

  const handleExport = async () => {
    const user = auth.currentUser;
    if (!user) return toast.error("Please log in to export.");

    let sd = statusData;
    if (!sd) {
      try { sd = await getCaseStatus(caseNumber); setStatusData(sd); }
      catch { return toast.error("Couldn't load the case to export."); }
    }
    if (!sd?.summary?.caseNumber) return toast.error("Nothing to export for this case.");

    const limit = await checkConsumerCaseLimit(globalUser, sd);
    if (!limit.canProceed) {
      setCaseLimitData({ currentCount: limit.currentCount, maxLimit: limit.maxLimit, planName: limit.planName });
      setShowCaseLimitDialog(true);
      return;
    }

    setExporting(true);
    try {
      const dup = await findDuplicateConsumerCase(sd.summary.caseNumber, user.uid);
      if (dup) { setDupInfo(dup); setDupOpen(true); return; }
      // Also pull the case-history timeline so the saved case has the Case History tab.
      let hd = historyData;
      if (!hd) { try { hd = await getCaseHistory(caseNumber); setHistoryData(hd); } catch { hd = null; } }
      await exportConsumerCase({ statusData: sd, historyData: hd, user, userMobile });
      setInfoOpen(true);
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const loadStatus = useCallback(async () => {
    if (statusData) return;
    setLoading(true);
    try {
      setStatusData(await getCaseStatus(caseNumber));
    } catch (err) {
      toast.error(err.message || "Failed to load case status");
    } finally {
      setLoading(false);
    }
  }, [caseNumber, statusData]);

  const loadHistory = useCallback(async () => {
    if (historyData) return;
    setLoading(true);
    try {
      setHistoryData(await getCaseHistory(caseNumber));
    } catch (err) {
      toast.error(err.message || "Failed to load case history");
    } finally {
      setLoading(false);
    }
  }, [caseNumber, historyData]);

  useEffect(() => {
    if (!caseNumber) return;
    if (tab === "status") loadStatus();
    else loadHistory();
  }, [tab, caseNumber, loadStatus, loadHistory]);

  if (!caseNumber) {
    return (
      <>
                <div className="relative min-h-screen flex flex-col items-center justify-center text-white">
          <div className="mb-4"><FindCaseStepper current={4} /></div>
          <p className="mb-4">No case selected.</p>
          <Link to="/portal/fetchfromejagriti" className="bg-yellow-500 text-[#060125] rounded py-2 px-6 font-bold">
            Go to e-Jagriti Search
          </Link>
        </div>
      </>
    );
  }

  const summary = statusData?.summary;
  const timeline = historyData?.timeline ? (order === "DESC" ? historyData.timeline : [...historyData.timeline].reverse()) : [];

  return (
    <>
                        <div className="relative min-h-screen flex flex-col">
          <div className="mb-4"><FindCaseStepper current={4} /></div>
        <main className="px-4 md:px-12 py-8">
          {/* Breadcrumb / header */}
          <div className="relative z-10 flex items-center justify-between p-2 mb-4">
            <button onClick={() => navigate("/portal/fetchfromejagriti")} className="flex items-center text-white">
              <ArrowLeft />
              <span className="text-yellow-500 ml-3 font-bold text-xl md:text-2xl">Case History / Status</span>
            </button>
            <div className="flex items-center gap-3">
              <span className="text-slate-500 text-sm hidden md:block">{caseNumber}</span>
              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-2 bg-white text-black py-2 px-5 rounded-full font-bold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UploadCloud className="h-4 w-4" /> {exporting ? "Importing…" : "Import Case"}
              </button>
            </div>
          </div>

          <div className="relative z-10 max-w-6xl mx-auto">
            {/* Tabs */}
            <div className="flex gap-1 border-b border-white/15 mb-5">
              {[{ id: "status", label: "CASE STATUS" }, { id: "history", label: "CASE HISTORY" }].map((tb) => (
                <button
                  key={tb.id}
                  onClick={() => { setTab(tb.id); setShowOrders(false); }}
                  className={`px-5 py-2.5 text-sm font-semibold tracking-wide transition-colors border-b-2 -mb-px ${
                    tab === tb.id ? "text-yellow-400 border-yellow-400" : "text-slate-500 border-transparent hover:text-white"
                  }`}
                >
                  {tb.label}
                </button>
              ))}
            </div>

            {loading && (
              <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
                <RefreshCw className="animate-spin h-5 w-5" /> Loading…
              </div>
            )}

            {/* ===== CASE STATUS ===== */}
            {tab === "status" && !loading && summary && !showOrders && (
              <div className="bg-white border border-slate-200 shadow-sm backdrop-blur-xl border border-white/15 rounded-2xl overflow-hidden">
                <div className="bg-white/5 px-5 py-3 border-b border-white/10">
                  <h2 className="text-white font-bold text-lg">Case Details</h2>
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5">
                  <Detail label="Case Number" value={summary.caseNumber} />
                  <Detail label="Filing Reference Number" value={summary.referenceNumber} />
                  <Detail label="Case Stage" value={summary.caseStage} />
                  <Detail label="Filed In" value={summary.filedIn} />
                  <Detail label="Complainant" value={summary.complainant} />
                  <Detail label="Opposite Party" value={summary.oppositeParty} />
                  <Detail label="Filed By" value={summary.filedBy} />
                  <Detail label="Purpose" value={summary.purpose} />
                  <Detail label="Filing Date" value={summary.filingDate} />
                  <Detail label="Next Hearing" value={summary.nextHearing} />
                  <Detail label="Application Filed" value={summary.applicationFiled} />
                  <Detail label="Attached Lower Court" value={summary.attachedLowerCourt} />
                </div>
                <div className="flex justify-end gap-3 px-5 py-4 border-t border-white/10">
                  <button
                    onClick={() => setShowOrders(true)}
                    className="bg-yellow-500 text-[#060125] rounded py-2 px-5 font-bold text-sm hover:bg-yellow-400"
                  >
                    VIEW HISTORY AND ORDER
                  </button>
                  <button
                    onClick={() => navigate("/portal/fetchfromejagriti")}
                    className="border border-white/30 text-white rounded py-2 px-5 font-semibold text-sm hover:bg-white/10"
                  >
                    BACK
                  </button>
                </div>
              </div>
            )}

            {/* ===== HISTORY & ORDER table ===== */}
            {tab === "status" && !loading && summary && showOrders && (
              <div className="bg-white border border-slate-200 shadow-sm backdrop-blur-xl border border-white/15 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
                  <h2 className="text-white font-bold">History &amp; Orders</h2>
                  <button onClick={() => setShowOrders(false)} className="text-yellow-400 text-sm font-semibold hover:underline">← Back to details</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-white/5 text-slate-500 text-left">
                        <th className="px-4 py-3 font-medium">No.</th>
                        <th className="px-4 py-3 font-medium">Date of Hearing</th>
                        <th className="px-4 py-3 font-medium">Case Stage</th>
                        <th className="px-4 py-3 font-medium">Date of Next Hearing</th>
                        <th className="px-4 py-3 font-medium">Courtroom Proceedings</th>
                        <th className="px-4 py-3 font-medium">Daily Order</th>
                        <th className="px-4 py-3 font-medium">Judgement</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {(showAllOrders ? (statusData.historyAndOrders || []) : (statusData.historyAndOrders || []).slice(0, 5)).map((h) => (
                        <tr key={h.sequenceNumber} className="text-white/85 hover:bg-white/5">
                          <td className="px-4 py-3">{h.sequenceNumber}</td>
                          <td className="px-4 py-3 text-emerald-300">{h.dateOfHearing || "N/A"}</td>
                          <td className="px-4 py-3">{h.caseStage || "N/A"}</td>
                          <td className="px-4 py-3 text-emerald-300">{h.dateOfNextHearing || "N/A"}</td>
                          <td className="px-4 py-3"><ViewBtn doc={h.courtroomProceedings} /></td>
                          <td className="px-4 py-3"><ViewBtn doc={h.dailyOrder} /></td>
                          <td className="px-4 py-3"><ViewBtn doc={h.judgement} /></td>
                        </tr>
                      ))}
                      {!(statusData.historyAndOrders || []).length && (
                        <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No hearings/orders found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/10">
                  <span className="text-slate-500 text-xs">
                    1 - {showAllOrders ? (statusData.historyAndOrders || []).length : Math.min(5, (statusData.historyAndOrders || []).length)} of {(statusData.historyAndOrders || []).length}
                  </span>
                  {(statusData.historyAndOrders || []).length > 5 && (
                    <button
                      onClick={() => setShowAllOrders((s) => !s)}
                      className="px-5 py-2 rounded-full border border-white/20 text-white/90 hover:bg-white/10 text-sm font-semibold transition-colors"
                    >
                      {showAllOrders ? "Show Less" : `View All (${(statusData.historyAndOrders || []).length})`}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ===== CASE HISTORY ===== */}
            {tab === "history" && !loading && historyData && (
              <>
                <div className="bg-white border border-slate-200 shadow-sm backdrop-blur-xl border border-white/15 rounded-2xl overflow-hidden mb-6">
                  <div className="bg-white/5 px-5 py-3 border-b border-white/10">
                    <h2 className="text-white font-bold text-lg">Case Details</h2>
                  </div>
                  <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5">
                    <Detail label="Case Number" value={historyData.caseDetails?.caseNumber} />
                    <Detail label="Reference Number" value={historyData.caseDetails?.referenceNumber} />
                    <Detail label="Complainant" value={historyData.caseDetails?.complainant} />
                    <Detail label="Opposite Party" value={historyData.caseDetails?.oppositeParty} />
                    <Detail label="Commission" value={historyData.caseDetails?.commission} />
                  </div>
                </div>

                {/* Order toggle */}
                <div className="flex justify-end items-center gap-2 mb-4">
                  <span className="text-slate-500 text-sm">Order By:</span>
                  <button
                    onClick={() => setOrder((o) => (o === "DESC" ? "ASC" : "DESC"))}
                    className="border border-white/30 text-white rounded px-3 py-1.5 text-sm font-semibold hover:bg-white/10"
                  >
                    {order}
                  </button>
                </div>

                {/* Zigzag timeline */}
                <ConsumerTimeline timeline={timeline} />
              </>
            )}
          </div>
        </main>
      </div>

      <InfoModal
        isOpen={infoOpen}
        onClose={() => setInfoOpen(false)}
        title="Information"
        message="Case exported successfully! A reminder is set for the next hearing date (if any)."
      />
      <InfoModal
        isOpen={dupOpen}
        onClose={() => setDupOpen(false)}
        title="Duplicate Case Found"
        message={`Case ${dupInfo?.caseNo} is already exported and saved in your profile under ${dupInfo?.collection} cases.`}
      />
      <CaseLimitDialog
        isOpen={showCaseLimitDialog}
        onClose={() => setShowCaseLimitDialog(false)}
        currentCount={caseLimitData.currentCount}
        maxLimit={caseLimitData.maxLimit}
        planName={caseLimitData.planName}
      />
    </>
  );
}
